import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Kanban, Lightbulb, Plus, Trash, CaretUp, Code, CheckCircle } from "@phosphor-icons/react";

const STATUSES = [
  { key: "backlog", label: "Backlog", color: "bg-gray-500/20 border-gray-500/40 text-gray-300" },
  { key: "doing", label: "En curso", color: "bg-blue-500/20 border-blue-500/40 text-blue-300" },
  { key: "review", label: "Review", color: "bg-yellow-500/20 border-yellow-500/40 text-yellow-300" },
  { key: "done", label: "Terminado", color: "bg-green-500/20 border-green-500/40 text-green-300" },
];

const PRIORITIES = {
  low: { label: "Baja", color: "text-gray-400" },
  med: { label: "Media", color: "text-blue-400" },
  high: { label: "Alta", color: "text-yellow-400" },
  urgent: { label: "Urgente", color: "text-red-400" },
};

export default function DevDashboard() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [showTask, setShowTask] = useState(null);
  const [showIdea, setShowIdea] = useState(null);

  const load = async () => {
    try {
      const [t, i] = await Promise.all([api.get("/dev/tasks"), api.get("/dev/ideas")]);
      setTasks(t.data.tasks || []);
      setIdeas(i.data.ideas || []);
    } catch (e) {
      toast.error("Error al cargar");
    }
  };
  useEffect(() => { load(); }, []);

  if (user && !["admin", "desarrollador"].includes(user.role)) return <Navigate to="/dashboard" replace />;

  const saveTask = async () => {
    try {
      if (showTask.id) {
        await api.put(`/dev/tasks/${showTask.id}`, showTask);
        toast.success("Tarea actualizada");
      } else {
        await api.post("/dev/tasks", showTask);
        toast.success("Tarea creada");
      }
      setShowTask(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const deleteTask = async (id) => {
    if (!window.confirm("¿Eliminar tarea?")) return;
    try { await api.delete(`/dev/tasks/${id}`); toast.success("Eliminada"); load(); } catch { toast.error("Error"); }
  };

  const changeStatus = async (task, newStatus) => {
    try {
      await api.put(`/dev/tasks/${task.id}`, { status: newStatus });
      load();
    } catch { toast.error("Error"); }
  };

  const saveIdea = async () => {
    try {
      if (showIdea.id) {
        await api.put(`/dev/ideas/${showIdea.id}`, showIdea);
        toast.success("Idea actualizada");
      } else {
        await api.post("/dev/ideas", showIdea);
        toast.success("Idea creada");
      }
      setShowIdea(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const voteIdea = async (id) => {
    try { await api.post(`/dev/ideas/${id}/vote`); load(); } catch { toast.error("Error"); }
  };

  const deleteIdea = async (id) => {
    if (!window.confirm("¿Eliminar idea?")) return;
    try { await api.delete(`/dev/ideas/${id}`); toast.success("Eliminada"); load(); } catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-4" data-testid="dev-dashboard-page">
      <div className="card-tactical p-4 bg-gradient-to-br from-blue-500/10 to-[#CCFF00]/5 border-blue-500/30">
        <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-blue-300 flex items-center gap-1">
          <Code size={12} weight="fill" /> Workspace de Desarrollo
        </p>
        <h1 className="font-['Barlow_Condensed'] text-4xl font-black text-white leading-none mt-1">Dev Dashboard</h1>
        <p className="text-xs text-gray-400 mt-1">Gestiona tareas e ideas del equipo de desarrollo. Solo accesible para admin y desarrolladores.</p>
      </div>

      <Tabs defaultValue="tasks">
        <TabsList className="bg-[#0A0A0A] border border-white/10">
          <TabsTrigger value="tasks" data-testid="tab-tasks"><Kanban size={14} className="mr-1" /> Tareas ({tasks.length})</TabsTrigger>
          <TabsTrigger value="ideas" data-testid="tab-ideas"><Lightbulb size={14} className="mr-1" /> Ideas ({ideas.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="mt-3 space-y-3">
          <Button onClick={() => setShowTask({ title: "", description: "", status: "backlog", priority: "med", tags: [] })} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="new-task-btn">
            <Plus size={14} className="mr-1" /> Nueva tarea
          </Button>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {STATUSES.map((s) => {
              const list = tasks.filter((t) => t.status === s.key);
              return (
                <div key={s.key} className="bg-[#0A0A0A] border border-white/10 rounded p-2" data-testid={`column-${s.key}`}>
                  <div className={`text-[10px] tracking-widest uppercase font-bold p-1.5 mb-2 rounded border ${s.color}`}>
                    {s.label} ({list.length})
                  </div>
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {list.map((t) => (
                      <div key={t.id} className="bg-black/40 border border-white/10 p-2 rounded hover:border-[#CCFF00]/40 transition-colors cursor-pointer" onClick={() => setShowTask({ ...t })} data-testid={`task-${t.id}`}>
                        <div className="flex items-start justify-between gap-1">
                          <p className="text-xs text-white font-semibold flex-1">{t.title}</p>
                          <span className={`text-[9px] font-bold uppercase ${PRIORITIES[t.priority]?.color || "text-gray-400"}`}>{PRIORITIES[t.priority]?.label || t.priority}</span>
                        </div>
                        {t.description && <p className="text-[10px] text-gray-400 mt-1 line-clamp-2">{t.description}</p>}
                        <div className="flex items-center justify-between mt-2 text-[9px] text-gray-500">
                          <span>{t.assigned_name || "—"}</span>
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <Select value={t.status} onValueChange={(v) => changeStatus(t, v)}>
                              <SelectTrigger className="h-5 bg-transparent border-white/10 text-[9px] py-0 px-1" data-testid={`status-select-${t.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[#0A0A0A] border-white/15">
                                {STATUSES.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    ))}
                    {list.length === 0 && <p className="text-[10px] text-gray-600 text-center py-3">Vacío</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="ideas" className="mt-3 space-y-3">
          <Button onClick={() => setShowIdea({ title: "", description: "", category: "feature" })} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="new-idea-btn">
            <Plus size={14} className="mr-1" /> Nueva idea
          </Button>

          {ideas.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">Sin ideas aún.</p>
          ) : (
            <div className="space-y-2">
              {ideas.map((i) => {
                const voted = (i.voted_by || []).includes(user?.id);
                return (
                  <div key={i.id} className="bg-[#0A0A0A] border border-white/10 p-3 rounded flex items-start gap-3" data-testid={`idea-${i.id}`}>
                    <button onClick={() => voteIdea(i.id)} className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded transition-colors ${voted ? "bg-[#CCFF00]/20 border border-[#CCFF00]/40" : "bg-white/5 border border-white/10 hover:border-[#CCFF00]/30"}`} data-testid={`vote-${i.id}`}>
                      <CaretUp size={14} weight="bold" className={voted ? "text-[#CCFF00]" : "text-gray-400"} />
                      <span className={`text-xs font-bold ${voted ? "text-[#CCFF00]" : "text-gray-400"}`}>{i.votes || 0}</span>
                    </button>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setShowIdea({ ...i })}>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-white font-bold">{i.title}</p>
                        <span className="text-[9px] uppercase font-bold text-blue-400 bg-blue-500/10 px-1 rounded">{i.category}</span>
                        {i.status !== "open" && <span className="text-[9px] uppercase font-bold text-green-400 bg-green-500/10 px-1 rounded">{i.status}</span>}
                      </div>
                      {i.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{i.description}</p>}
                      <p className="text-[9px] text-gray-500 mt-1">{i.created_by_name} · {new Date(i.created_at).toLocaleDateString("es-MX")}</p>
                    </div>
                    <Button onClick={() => deleteIdea(i.id)} size="sm" variant="outline" className="border-red-500/30 text-red-400 h-7 w-7 p-0"><Trash size={12} /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Task editor */}
      {showTask && (
        <Dialog open={!!showTask} onOpenChange={(o) => !o && setShowTask(null)}>
          <DialogContent className="max-w-xl bg-[#0A0A0A] border-[#CCFF00]/30">
            <DialogHeader><DialogTitle className="text-white">{showTask.id ? "Editar tarea" : "Nueva tarea"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input value={showTask.title} onChange={(e) => setShowTask({ ...showTask, title: e.target.value })} placeholder="Título" className="bg-[#0A0A0A] border-white/15" data-testid="task-title" maxLength={200} />
              <textarea value={showTask.description || ""} onChange={(e) => setShowTask({ ...showTask, description: e.target.value })} placeholder="Descripción (opcional)" className="w-full bg-[#0A0A0A] border border-white/15 rounded p-2 text-xs text-gray-200 min-h-[100px]" data-testid="task-desc" maxLength={5000} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Estado</Label>
                  <Select value={showTask.status} onValueChange={(v) => setShowTask({ ...showTask, status: v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="task-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/15">{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Prioridad</Label>
                  <Select value={showTask.priority} onValueChange={(v) => setShowTask({ ...showTask, priority: v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="task-priority"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/15">{Object.entries(PRIORITIES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={saveTask} disabled={!showTask.title?.trim()} className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="task-save"><CheckCircle size={14} className="mr-1" /> Guardar</Button>
                {showTask.id && <Button onClick={() => { deleteTask(showTask.id); setShowTask(null); }} variant="outline" className="border-red-500/30 text-red-400"><Trash size={14} /></Button>}
                <Button onClick={() => setShowTask(null)} variant="outline" className="border-white/15">Cancelar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Idea editor */}
      {showIdea && (
        <Dialog open={!!showIdea} onOpenChange={(o) => !o && setShowIdea(null)}>
          <DialogContent className="max-w-xl bg-[#0A0A0A] border-[#CCFF00]/30">
            <DialogHeader><DialogTitle className="text-white">{showIdea.id ? "Editar idea" : "Nueva idea"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input value={showIdea.title} onChange={(e) => setShowIdea({ ...showIdea, title: e.target.value })} placeholder="Título" className="bg-[#0A0A0A] border-white/15" data-testid="idea-title" maxLength={200} />
              <textarea value={showIdea.description || ""} onChange={(e) => setShowIdea({ ...showIdea, description: e.target.value })} placeholder="Describe la idea..." className="w-full bg-[#0A0A0A] border border-white/15 rounded p-2 text-xs text-gray-200 min-h-[100px]" data-testid="idea-desc" maxLength={5000} />
              <Select value={showIdea.category} onValueChange={(v) => setShowIdea({ ...showIdea, category: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/15" data-testid="idea-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15">
                  <SelectItem value="feature">Feature</SelectItem>
                  <SelectItem value="bug">Bug</SelectItem>
                  <SelectItem value="ux">UX/UI</SelectItem>
                  <SelectItem value="performance">Performance</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-2 pt-2">
                <Button onClick={saveIdea} disabled={!showIdea.title?.trim()} className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="idea-save"><CheckCircle size={14} className="mr-1" /> Guardar</Button>
                <Button onClick={() => setShowIdea(null)} variant="outline" className="border-white/15">Cancelar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
